import type { Instance, Props } from "tippy.js"
import "tippy.js/dist/tippy.css"
import { getProLandingUrl } from "@/helpers/APIHelper"
import { isProEnabled } from "@/helpers/FeatureFlagHelper"

type PaywallContext = {
  isAuthenticated: () => boolean
  t: (key: string) => string
  onLogin: () => void
}

type PaywallProps = Partial<Props> & {
  onTrigger?: (instance: Instance, event: Event) => void
}

const MASK_CHAR = "░"

const isMaskedValue = (value: string | null | undefined): boolean => {
  return isProEnabled && typeof value === "string" && value.includes(MASK_CHAR)
}

const createPaywallContent = (context: PaywallContext, close: () => void): HTMLElement => {
  const isAuthenticated = context.isAuthenticated()
  const card = document.createElement("div")
  card.className = "paywall-card"

  const title = document.createElement("h3")
  title.className = "paywall-card__title"
  title.textContent = context.t(isAuthenticated ? "paywall.title" : "paywall.title_anon")

  const text = document.createElement("p")
  text.className = "paywall-card__text"
  text.textContent = context.t(isAuthenticated ? "paywall.text" : "paywall.text_anon")

  card.append(title, text)

  if (isAuthenticated) {
    const link = document.createElement("a")
    link.className = "btn btn-blue paywall-card__button"
    link.href = getProLandingUrl()
    link.target = "_blank"
    link.rel = "noopener"
    link.textContent = context.t("paywall.button")
    link.addEventListener("click", close)
    card.append(link)
  } else {
    const button = document.createElement("button")
    button.className = "btn btn-blue paywall-card__button"
    button.type = "button"
    button.textContent = context.t("auth_modal.btn")
    button.addEventListener("click", () => {
      close()
      context.onLogin()
    })
    card.append(button)
  }

  return card
}

const createPaywallTippyProps = (context: PaywallContext, props: PaywallProps = {}): Partial<Props> => {
  const onShow = props.onShow

  return {
    content: document.createElement("div"),
    interactive: true,
    interactiveBorder: 24,
    delay: [100, 500],
    placement: "top-start",
    arrow: false,
    theme: "tender-paywall",
    maxWidth: "none",
    appendTo: () => document.body,
    zIndex: 9999,
    ...props,
    onShow(instance) {
      instance.setContent(createPaywallContent(context, () => instance.hide()))
      return onShow ? onShow(instance) : undefined
    },
  }
}

const cursorReference = (event: MouseEvent): (() => DOMRect) => {
  const x = event.clientX
  const y = event.clientY
  return () => new DOMRect(x, y, 0, 0)
}

export {
  PaywallContext,
  PaywallProps,
  createPaywallTippyProps,
  cursorReference,
  isMaskedValue,
}
